import {useEffect, useRef, useState} from "react";

import {useAuth} from "../AuthContext";
import {useApi, usePlayMakersConfig} from "../PlayMakersProvider";
import {ExtendedNotificationType, NotificationType} from "../types/notifications.types";
import {useInterval} from "./useInterval";
import {usePagination} from "./usePagination";

type NotificationMarkAllAsReadMethod = () => Promise<void>;
type NotificationFetchMethod = (quiet?: boolean) => Promise<NotificationType[]>;
type NotificationFetchMoreMethod = (refresh?: boolean) => Promise<void>;

interface UseNotificationsReturn {
  fetched: boolean;
  notifications: ExtendedNotificationType[];
  toNotify: ExtendedNotificationType[];
  currentPage: number;
  markAllAsRead: NotificationMarkAllAsReadMethod;
  refetch: NotificationFetchMethod;
  fetchMore: NotificationFetchMoreMethod;
}

interface useNotificationsParams {
  params?: Partial<{
    from: string;
    importance: string
    type: string
    page: number,
    limit: number
  }>,
  poll?: boolean,
}

export const useNotifications = ({
  params,
  poll = false
}: useNotificationsParams = {}): UseNotificationsReturn => {
  const {userData} = useAuth();
  const {
    getNotifications,
    markAllNotificationsAsRead,
    updateNotification,
    deleteNotification
  } = useApi();
  const {NOTIFICATIONS_POLL_PERIOD} = usePlayMakersConfig();
  const [fetched, setFetched] = useState<boolean>(false);

  const [notifications, setNotifications] = useState<ExtendedNotificationType[]>([]);
  const [toNotify, setToNotify] = useState<ExtendedNotificationType[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(true);

  const {appendNextPage, refreshLastPage, checkHasMore, currentPage} =
    usePagination<ExtendedNotificationType>(setNotifications, setHasMore, hasMore, params?.page, params?.limit);

  const shouldStopPolling = useRef<boolean>(true);

  const lastNotificationDate = useRef<string>((new Date).toISOString());

  const formatNotifications = (notifications: NotificationType[]): ExtendedNotificationType[] =>
    notifications.map(n => ({
      ...n,
      delete: async() => {
        try {
          (n as ExtendedNotificationType).isDeleted = true;
          await deleteNotification(n.id);
        } catch (error) {
          console.error(`Error deleting notification ${n.id}.`, error);
          (n as ExtendedNotificationType).isDeleted = false;
        }
      },
      isDeleted: false,
      markAsSeen: async() => {
        try {
          await updateNotification(n.id, {seen: true});
          n.seen = true;
        } catch (error) {
          console.error(
            `Error marking notification ${n.id} as seen.`,
            error
          );
        }
      }}));


  // Fetch the notifications from the backend
  const fetchNotifications: NotificationFetchMethod =
    async(quiet = false) => {
      setFetched(quiet);
      try {
        const fetchedNotifications = checkHasMore(formatNotifications(await getNotifications(params)));
        setNotifications(fetchedNotifications);
        return fetchedNotifications;
      } catch (error) {
        console.error("Error fetching notifications", error);
        throw new Error("Error fetching notifications", {cause: error});
      } finally {
        setFetched(true);
      }
    };

  const fetchMore: NotificationFetchMoreMethod = async refresh => {
    try {
      setFetched(false);
      if(refresh) {
        await refreshLastPage(async appendParams =>
          formatNotifications(await getNotifications({...params, ...appendParams})));
      } else {
        await appendNextPage(async appendParams =>
          formatNotifications(await getNotifications({...params, ...appendParams})));
      }
    } catch (error) {
      console.error("Error fetching more projects", error);
      throw new Error("Error fetching more projects", {cause: error});
    } finally {
      setFetched(true);
    }
  };

  const markAllAsRead = async() => {
    try {
      await markAllNotificationsAsRead();
      setNotifications(notifications.map(n => ({...n, seen: true})));
    } catch (error) {
      console.error("Error marking all notifications as read", error);
      throw new Error("Error marking all notifications as read", {cause: error});
    }
  };

  const pollNotifications = async() => {
    if(shouldStopPolling.current || !poll) return;

    try {
      const fetchedNotifications = formatNotifications(await getNotifications({
        ...params,
        startDate: lastNotificationDate.current
      }));

      setToNotify(fetchedNotifications);

      if(fetchedNotifications.length > 0) {
        lastNotificationDate.current =
              new Date(new Date(fetchedNotifications[0].createdAt).getTime() + 1).toISOString();
      }

    } catch (error) {
      console.error("Error fetching notifications", error);
    }
  };

  useInterval(pollNotifications,
    shouldStopPolling.current || !poll ? null : NOTIFICATIONS_POLL_PERIOD > 500 ? NOTIFICATIONS_POLL_PERIOD : 500
  );

  useEffect(() => {
    if(userData) {
      shouldStopPolling.current = false;
      fetchNotifications();
    } else shouldStopPolling.current = true;

    return () => {
      shouldStopPolling.current = true;
      setNotifications([]);
    };
  }, [userData]);

  return {
    currentPage,
    fetchMore,
    fetched,
    markAllAsRead,
    notifications: notifications.filter((x: any) => !x.isDeleted),
    refetch: fetchNotifications,
    toNotify
  };
};
